/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 */
class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 */
class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { shadowRoot } = this;

    if (!shadowRoot) return;

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      return;
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.addEventListener('reflow', (event) => {
      this.#reflowItems(event.detail?.lastVisibleElement);
    });

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        this.removeAttribute('defer');
        this.#reflowItems();
      };
      const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb) => setTimeout(cb, 50);
      idleCallback(() => this.schedule(deferredReflow));
    } else {
      this.#reflowItems();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  get schedule() {
    return (callback) => {
      if (typeof window.requestAnimationFrame === 'function') {
        const id = window.requestAnimationFrame(callback);
        // WebKit fallback timer in case rAF drops inside In-App WebViews
        setTimeout(() => {
          try { callback(); } catch (e) {}
        }, 100);
        return id;
      }
      return setTimeout(callback, 16);
    };
  }

  #scheduled = false;

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    return this.#refs?.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs?.defaultSlot;
  }

  #lastDimensions = {
    width: null,
    height: null,
  };

  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        return;
      }
      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  #moveItemsToDefaultSlot() {
    if (!this.#refs) return;
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name || '';
    }
  }

  #reset() {
    if (!this.#refs) return;
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  showAll() {
    if (!this.#refs) return;
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #reflowItems = (lastVisibleElement) => {
    if (!this.#refs || !this.isConnected) return;
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};
    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    moreSlot.hidden = true;
    placeholder.hidden = true;

    const rootRect = list.getBoundingClientRect();
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    if (visibleElements.length !== elements.length || lastVisibleElement) {
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height || 40}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      visibleElements = getVisibleElements();

      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          visibleElements.splice(visibleIndex, 1);
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? (overflowSlot.name || 'overflow') : (defaultSlot.name || '');
    }

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    if (hasOverflow) {
      this.#updateMinimumReached(visibleElements);
    }

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  #refs;
  #resizeObserver = new ResizeNotifier(this.#handleChange);
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  return totalHeight;
}

(() => {
  const initHeaderHeights = () => {
    const header = document.querySelector('header-component') || document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const headerHeight = header.offsetHeight;
    const headerGroupHeight = calculateHeaderGroupHeight(header);

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderHeights);
  } else {
    initHeaderHeights();
  }
})();

(() => {
  const initOffset = () => {
    const header = document.querySelector('#header-component');
    const headerGroup = document.querySelector('#header-group');
    const hasHeaderSection = headerGroup?.querySelector('.header-section');
    if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
      return;
    }

    const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
    const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
    document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOffset);
  } else {
    initOffset();
  }
})();